import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import {
  ReturnsSortingMode,
  returnsSortingModeLabels,
} from "@redotech/redo-model/returns/sorting-types";
import {
  ReFulfillmentBillingConfig,
  ReFulfillmentBillingMode,
  ReFulfillmentShippingMarkupConfig,
  TeamType,
} from "@redotech/redo-model/team";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { Text } from "@redotech/redo-web/text";
import {
  groupInput,
  input,
  InputProvider,
  numberValidator,
} from "@redotech/ui/form";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { RedoAdminClientContext } from "../../client/context";
import { getTeam, updateTeam } from "../../client/team";
import { automationUrl } from "../merchant-app";
import { LoadTeamContext, TeamContext } from "../team";
import * as styles from "./logistics.module.css";
import { LocationsCard } from "./logistics/locations-card";
import { LogisticsProductSummary } from "./logistics/logistics-product-summary";
import { SettingCard } from "./returns-package-protection/v2/component/setting-card";
import { SettingSection } from "./returns-package-protection/v2/component/setting-section";
import { SettingSectionTabNav } from "./returns-package-protection/v2/component/setting-section-tab-nav";
import {
  redoManagedReturnsFormValueToTeam,
  RedoManagedReturnsSettingsCardV2,
  redoManagedReturnsSettingsDefault,
  redoManagedReturnsSettingsForm,
  redoManagedReturnsTeamToFormValue,
} from "./returns-package-protection/v2/redo-managed-returns-settings";
import {
  TabNavigationContext,
  TabNavigationProvider,
} from "./returns-package-protection/v2/tab-navigation-provider";

const reFulfillmentBillingConfigForm = groupInput({
  billingMode: input<"percentage" | "fixed">(),
  revSharePercentage: input<number>(),
  fixedFeeAmount: input<string>({ validator: numberValidator({ min: 0 }) }),
});

const shippingMarkupConfigForm = groupInput({
  markupMode: input<"percentage" | "fixed">(),
  markupPercentage: input<number>(),
  markupAmount: input<string>({ validator: numberValidator({ min: 0 }) }),
});

const reFulfillmentForm = groupInput({
  enabled: input<boolean>(),
  redoOwned: reFulfillmentBillingConfigForm,
  merchantOwned: reFulfillmentBillingConfigForm,
  shippingMarkup: shippingMarkupConfigForm,
});

type ReFulfillmentValue = InputProvider.Value<typeof reFulfillmentForm>;

const billingConfigDefault: InputProvider.Value<
  typeof reFulfillmentBillingConfigForm
> = { billingMode: "percentage", revSharePercentage: 50, fixedFeeAmount: "0" };

const shippingMarkupDefault: InputProvider.Value<
  typeof shippingMarkupConfigForm
> = { markupMode: "percentage", markupPercentage: 10, markupAmount: "0" };

const reFulfillmentDefault: ReFulfillmentValue = {
  enabled: false,
  redoOwned: billingConfigDefault,
  merchantOwned: billingConfigDefault,
  shippingMarkup: shippingMarkupDefault,
};

const logisticsForm = groupInput(
  {
    redoManagedReturnsSettings: redoManagedReturnsSettingsForm,
    reFulfillment: reFulfillmentForm,
    customsBrokerClientNumber: input<string>(),
    sortingMode: input<ReturnsSortingMode>(),
  },
  {
    validator: (value) => {
      if (
        value.redoManagedReturnsSettings.enabled &&
        value.redoManagedReturnsSettings.availableReturnsCenters.length === 0
      ) {
        return [
          "At least one returns center must be configured when Redo Managed Returns is enabled",
        ];
      }
      return [];
    },
  },
);

type LogisticsValue = InputProvider.Value<typeof logisticsForm>;

const logisticsDefault: LogisticsValue = {
  redoManagedReturnsSettings: redoManagedReturnsSettingsDefault,
  reFulfillment: reFulfillmentDefault,
  customsBrokerClientNumber: "",
  sortingMode: ReturnsSortingMode.SERVICE_LEVEL,
};

export const LogisticsPage = memo(function LogisticsPage() {
  const params = useParams();
  const teamId = params.teamId!;

  const team = useContext(TeamContext);
  const loadTeam = useRequiredContext(LoadTeamContext);
  const client = useRequiredContext(RedoAdminClientContext);
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const [automationResult, setAutomationResult] = useState<{
    automationId: string;
    entries: { accountName: string; locationNames: string[] }[];
  } | null>(null);

  const [existingAutomationId, setExistingAutomationId] = useState<
    string | null
  >(null);

  // Fetch existing automation on mount
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { automationId } = await rpcClient.getRmrAutomations({ teamId });
        if (!cancelled && automationId) {
          setExistingAutomationId(automationId);
        }
      } catch {
        // Ignore — no automation exists
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rpcClient, teamId]);

  const value: LogisticsValue | undefined = useMemo(() => {
    if (!team) {
      return;
    }

    return {
      redoManagedReturnsSettings: redoManagedReturnsTeamToFormValue(team),
      reFulfillment: {
        enabled: !!team.settings.reFulfillment?.enabled,
        redoOwned: billingConfigFromTeam(
          team.settings.reFulfillment?.redoOwned,
        ),
        merchantOwned: billingConfigFromTeam(
          team.settings.reFulfillment?.merchantOwned,
        ),
        shippingMarkup: shippingMarkupFromTeam(
          team.settings.reFulfillment?.shippingMarkup,
        ),
      },
      customsBrokerClientNumber: team.customsBrokerClientNumber ?? "",
      sortingMode: team.sortingMode ?? ReturnsSortingMode.SERVICE_LEVEL,
    };
  }, [team]);

  const formInput = useInput(logisticsForm, value || logisticsDefault);

  const coveragePriceLimit = useMemo(() => {
    const coveragePrice = parseFloat(team?.pricePerOrder || "0") || 0;
    const merchantBilledPrice =
      parseFloat(team?.merchantPricePerOrder || "0") || 0;
    return Math.max(coveragePrice, merchantBilledPrice);
  }, [team?.pricePerOrder, team?.merchantPricePerOrder]);

  const save: PageFormSave = useHandler(async (signal) => {
    const formValue = formInput.value;
    const totalCoverageShare =
      (Number(formValue.redoManagedReturnsSettings.processingCoverageShare) ||
        0) +
      (Number(
        formValue.redoManagedReturnsSettings.consolidationCoverageShare,
      ) || 0);
    if (totalCoverageShare > coveragePriceLimit) {
      return;
    }

    const currentTeam = await getTeam(client, { teamId, signal });

    // Detect newly enabled refulfillment centers before mutating currentTeam.
    // Both sides are in formatted ID space (rctr_...) from redoManagedReturnsTeamToFormValue.
    const previouslyEnabledIds = new Set(
      redoManagedReturnsTeamToFormValue(currentTeam)
        .availableReturnsCenters.filter((c) => c.refulfillment)
        .map((c) => c.returnsCenterId),
    );
    const newlyEnabledCenters =
      formValue.redoManagedReturnsSettings.availableReturnsCenters.filter(
        (c) => c.refulfillment && !previouslyEnabledIds.has(c.returnsCenterId),
      );

    currentTeam.settings.redoManagedReturns = redoManagedReturnsFormValueToTeam(
      formValue.redoManagedReturnsSettings,
    );

    currentTeam.settings.reFulfillment = {
      ...currentTeam.settings.reFulfillment,
      enabled: formValue.reFulfillment.enabled,
      redoOwned: billingConfigToTeam(formValue.reFulfillment.redoOwned),
      merchantOwned: billingConfigToTeam(formValue.reFulfillment.merchantOwned),
      shippingMarkup: shippingMarkupToTeam(
        formValue.reFulfillment.shippingMarkup,
      ),
    };

    currentTeam.customsBrokerClientNumber =
      formValue.customsBrokerClientNumber || undefined;

    currentTeam.sortingMode = formValue.sortingMode;

    await updateTeam(client, { teamId, team: currentTeam, signal });

    loadTeam();

    // If new refulfillment centers were enabled and an automation already
    // exists, warn the user to add them manually
    if (newlyEnabledCenters.length > 0) {
      const returnsCenterIds = newlyEnabledCenters.map(
        (c) => c.returnsCenterId,
      );
      const { automationId, locationsByAccount } =
        await rpcClient.getRmrAutomations({ teamId, returnsCenterIds });
      if (automationId) {
        setExistingAutomationId(automationId);
        setAutomationResult({
          automationId,
          entries: locationsByAccount ?? [],
        });
      }
    }
  });

  const handleCloseAutomationResult = useCallback(
    () => setAutomationResult(null),
    [],
  );

  return (
    <>
      {value && (
        <LogisticsForm
          existingAutomationId={existingAutomationId}
          id={teamId}
          input={formInput}
          save={save}
          value={value}
        />
      )}
      {automationResult && (
        <AutomationExistsModal
          automationId={automationResult.automationId}
          entries={automationResult.entries}
          onClose={handleCloseAutomationResult}
          teamId={teamId}
        />
      )}
    </>
  );
});

const LogisticsForm = memo(function LogisticsForm({
  id,
  input,
  save,
  value,
  existingAutomationId,
}: {
  id: string;
  input: InputProvider.Form<typeof logisticsForm>;
  save: PageFormSave;
  value: LogisticsValue | undefined;
  existingAutomationId: string | null;
}) {
  const team = useContext(TeamContext);
  const is3pl = team?.teamType === TeamType.FulfillmentService;

  const coveragePriceLimit = useMemo(() => {
    const coveragePrice = parseFloat(team?.pricePerOrder || "0") || 0;
    const merchantBilledPrice =
      parseFloat(team?.merchantPricePerOrder || "0") || 0;
    return Math.max(coveragePrice, merchantBilledPrice);
  }, [team?.pricePerOrder, team?.merchantPricePerOrder]);

  const {
    redoManagedReturnsSettings,
    reFulfillment,
    customsBrokerClientNumber,
    sortingMode,
  } = input.inputs;

  const rmrErrors = useMemo(() => {
    const errors = [...input.allErrors];
    const totalShare =
      (Number(redoManagedReturnsSettings.value.processingCoverageShare) || 0) +
      (Number(redoManagedReturnsSettings.value.consolidationCoverageShare) ||
        0);
    if (totalShare > coveragePriceLimit) {
      errors.push(
        `Coverage shares ($${totalShare.toFixed(2)}) exceed coverage limit ($${coveragePriceLimit.toFixed(2)})`,
      );
    }
    return errors;
  }, [
    input.allErrors,
    redoManagedReturnsSettings.value.processingCoverageShare,
    redoManagedReturnsSettings.value.consolidationCoverageShare,
    coveragePriceLimit,
  ]);

  return (
    <TabNavigationProvider>
      <div className={styles.pageContainer}>
        <SettingSectionTabNav />
        <div className={styles.settingsContent}>
          <ErrorBanner />
          <PageForm
            initial={value || logisticsDefault}
            input={input}
            save={save}
          >
            <SettingSection description="" id="products" title="Products">
              <SettingCard>
                <Flex align="stretch" dir="column" gap="none">
                  <LogisticsProductSummary
                    description="Charge merchants for managed returns processing"
                    productEnabled={redoManagedReturnsSettings.value.enabled}
                    setProductEnabled={
                      redoManagedReturnsSettings.inputs.enabled.setValue
                    }
                    title="Redo Managed Returns"
                  />
                </Flex>
              </SettingCard>
            </SettingSection>
            <SettingSection
              collapsible
              description="Managed returns processing, billing, and returns centers"
              errors={rmrErrors}
              id="redo-managed-returns"
              title="Redo Managed Returns"
            >
              <RedoManagedReturnsSettingsCardV2
                coveragePriceLimit={coveragePriceLimit}
                existingAutomationId={existingAutomationId}
                input={redoManagedReturnsSettings}
                teamId={id}
              />
            </SettingSection>
            <SettingSection
              collapsible
              defaultCollapsed
              description="Outbound shipping label markup configuration"
              id="outbound-shipping"
              title="Outbound"
            >
              <SettingCard>
                <Flex dir="column" gap="lg" p="xl">
                  <Flex dir="column" gap="xs">
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      textColor="primary"
                    >
                      Shipping label markup
                    </Text>
                    <Text fontSize="xs" textColor="tertiary">
                      Markup added to shipping label cost when billing the
                      merchant. Does not apply to UPS or USPS labels
                      (revenue-share agreements).
                    </Text>
                  </Flex>
                  <ShippingMarkupSection
                    disabled={false}
                    input={reFulfillment.inputs.shippingMarkup}
                  />
                </Flex>
              </SettingCard>
            </SettingSection>
            <SettingSection
              collapsible
              defaultCollapsed
              description="Billing for items fulfilled from Returns Centers"
              id="re-fulfillment"
              title="Re-fulfillment"
            >
              <ReFulfillmentBillingCard
                disabled={!reFulfillment.value.enabled}
                merchantOwned={reFulfillment.inputs.merchantOwned}
                redoOwned={reFulfillment.inputs.redoOwned}
              />
            </SettingSection>
            <SettingSection
              collapsible
              defaultCollapsed
              description="Customs and cross-border settings"
              id="cross-border"
              title="Cross-Border"
            >
              <SettingCard>
                <Flex dir="column" gap="lg" p="xl">
                  <RedoTextInput
                    label="Customs Broker Client Number"
                    setValue={customsBrokerClientNumber.setValue}
                    value={customsBrokerClientNumber.value}
                  />
                </Flex>
              </SettingCard>
            </SettingSection>
          </PageForm>
          {is3pl && (
            <SettingSection
              collapsible
              defaultCollapsed
              description="Sorting configuration for this 3PL's returns centers"
              id="sorting"
              title="Sorting"
            >
              <SortingModeCard sortingMode={sortingMode} />
            </SettingSection>
          )}
          {is3pl && (
            <SettingSection
              collapsible
              defaultCollapsed
              description="Manage which locations are designated as returns centers"
              id="locations"
              title="Locations"
            >
              <LocationsCard teamId={id} />
            </SettingSection>
          )}
        </div>
      </div>
    </TabNavigationProvider>
  );
});

const sortingModeOptions: RedoListItem<ReturnsSortingMode>[] = Object.values(
  ReturnsSortingMode,
).map((mode) => ({
  id: mode,
  type: "text" as const,
  text: returnsSortingModeLabels[mode],
  value: mode,
}));

const SortingModeCard = memo(function SortingModeCard({
  sortingMode,
}: {
  sortingMode: InputProvider.Form<ReturnType<typeof input<ReturnsSortingMode>>>;
}) {
  const selectedOption = useMemo(
    () =>
      sortingModeOptions.find((o) => o.value === sortingMode.value) ??
      sortingModeOptions[0],
    [sortingMode.value],
  );

  const handleSelected = useCallback(
    ({ item }: { item: RedoListItem<ReturnsSortingMode> }) => {
      if (item.value) {
        sortingMode.setValue(item.value);
      }
    },
    [sortingMode],
  );

  return (
    <SettingCard>
      <Flex dir="column" gap="lg" p="xl">
        <RedoSingleSelectDropdownInput
          label="Sorting mode"
          options={sortingModeOptions}
          optionSelected={handleSelected}
          selectedItem={selectedOption}
          size="xs"
        />
      </Flex>
    </SettingCard>
  );
});

function billingConfigFromTeam(
  config: ReFulfillmentBillingConfig | undefined,
): InputProvider.Value<typeof reFulfillmentBillingConfigForm> {
  return {
    billingMode: config?.billingMode ?? "percentage",
    revSharePercentage: config?.revSharePercentage ?? 50,
    fixedFeeAmount: (config?.fixedFeeAmount ?? 0).toString(),
  };
}

function billingConfigToTeam(
  config: InputProvider.Value<typeof reFulfillmentBillingConfigForm>,
): ReFulfillmentBillingConfig {
  return {
    billingMode: config.billingMode as ReFulfillmentBillingMode,
    revSharePercentage: config.revSharePercentage,
    fixedFeeAmount: Number(config.fixedFeeAmount) || 0,
  };
}

function shippingMarkupFromTeam(
  config: ReFulfillmentShippingMarkupConfig | undefined,
): InputProvider.Value<typeof shippingMarkupConfigForm> {
  return {
    markupMode: config?.markupMode ?? "percentage",
    markupPercentage: config?.markupPercentage ?? 0,
    markupAmount: (config?.markupAmount ?? 0).toString(),
  };
}

function shippingMarkupToTeam(
  config: InputProvider.Value<typeof shippingMarkupConfigForm>,
): ReFulfillmentShippingMarkupConfig {
  return {
    markupMode: config.markupMode as ReFulfillmentBillingMode,
    markupPercentage: config.markupPercentage,
    markupAmount: Number(config.markupAmount) || 0,
  };
}

const ReFulfillmentBillingSection = memo(function ReFulfillmentBillingSection({
  disabled,
  input,
}: {
  disabled: boolean;
  input: InputProvider.Form<typeof reFulfillmentBillingConfigForm>;
}) {
  const options: RadioGroupOption<"percentage" | "fixed">[] = useMemo(
    () => [
      {
        id: "percentage" as const,
        label: "Percentage of order value",
        childrenRenderLocation: "right" as const,
        children: (
          <RedoIncrementDecrement
            disabled={
              disabled || input.inputs.billingMode.value !== "percentage"
            }
            max={100}
            min={0}
            setValue={input.inputs.revSharePercentage.setValue}
            suffix="%"
            value={input.inputs.revSharePercentage.value}
          />
        ),
      },
      {
        id: "fixed" as const,
        label: "Fixed fee per fulfillment",
        childrenRenderLocation: "right" as const,
        children: (
          <RedoTextInput
            IconTrailing={() => <span>USD</span>}
            min={0}
            placeholder="0.00"
            setValue={input.inputs.fixedFeeAmount.setValue}
            size="sm"
            state={
              disabled || input.inputs.billingMode.value !== "fixed"
                ? "disabled"
                : "default"
            }
            step={0.01}
            value={input.inputs.fixedFeeAmount.value}
          />
        ),
      },
    ],
    [disabled, input.inputs],
  );

  return (
    <RedoRadioGroup<"percentage" | "fixed">
      options={options}
      setValue={input.inputs.billingMode.setValue}
      value={input.inputs.billingMode.value}
    />
  );
});

const ShippingMarkupSection = memo(function ShippingMarkupSection({
  disabled,
  input,
}: {
  disabled: boolean;
  input: InputProvider.Form<typeof shippingMarkupConfigForm>;
}) {
  const options: RadioGroupOption<"percentage" | "fixed">[] = useMemo(
    () => [
      {
        id: "percentage" as const,
        label: "Percentage of label cost",
        childrenRenderLocation: "right" as const,
        children: (
          <RedoIncrementDecrement
            disabled={
              disabled || input.inputs.markupMode.value !== "percentage"
            }
            max={1000}
            min={0}
            setValue={input.inputs.markupPercentage.setValue}
            suffix="%"
            value={input.inputs.markupPercentage.value}
          />
        ),
      },
      {
        id: "fixed" as const,
        label: "Fixed fee per label",
        childrenRenderLocation: "right" as const,
        children: (
          <RedoTextInput
            IconTrailing={() => <span>USD</span>}
            min={0}
            placeholder="0.00"
            setValue={input.inputs.markupAmount.setValue}
            size="sm"
            state={
              disabled || input.inputs.markupMode.value !== "fixed"
                ? "disabled"
                : "default"
            }
            step={0.01}
            value={input.inputs.markupAmount.value}
          />
        ),
      },
    ],
    [disabled, input.inputs],
  );

  return (
    <RedoRadioGroup<"percentage" | "fixed">
      options={options}
      setValue={input.inputs.markupMode.setValue}
      value={input.inputs.markupMode.value}
    />
  );
});

const CHEVRON_BASE_REFULFILLMENT: React.CSSProperties = {
  width: 20,
  height: 20,
  transition: "transform 0.2s ease",
  color: "var(--redo-colors-text-text-tertiary-600)",
  flexShrink: 0,
};

const ReFulfillmentBillingCard = memo(function ReFulfillmentBillingCard({
  disabled,
  redoOwned,
  merchantOwned,
}: {
  disabled: boolean;
  redoOwned: InputProvider.Form<typeof reFulfillmentBillingConfigForm>;
  merchantOwned: InputProvider.Form<typeof reFulfillmentBillingConfigForm>;
}) {
  const [redoOwnedOpen, setRedoOwnedOpen] = useState(true);
  const [merchantOwnedOpen, setMerchantOwnedOpen] = useState(true);

  return (
    <SettingCard>
      <div
        style={{
          borderBottom: "1px solid var(--redo-colors-border-secondary)",
        }}
      >
        <Flex
          align="center"
          justify="space-between"
          onClick={() => setRedoOwnedOpen((v) => !v)}
          p="xl"
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Redo-owned inventory
          </Text>
          <ChevronRightIcon
            style={{
              ...CHEVRON_BASE_REFULFILLMENT,
              transform: redoOwnedOpen ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />
        </Flex>
        {redoOwnedOpen && (
          <Flex pb="xl" pl="xl" pr="xl">
            <ReFulfillmentBillingSection
              disabled={disabled}
              input={redoOwned}
            />
          </Flex>
        )}
      </div>
      <div>
        <Flex
          align="center"
          justify="space-between"
          onClick={() => setMerchantOwnedOpen((v) => !v)}
          p="xl"
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Merchant-owned inventory
          </Text>
          <ChevronRightIcon
            style={{
              ...CHEVRON_BASE_REFULFILLMENT,
              transform: merchantOwnedOpen ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />
        </Flex>
        {merchantOwnedOpen && (
          <Flex pb="xl" pl="xl" pr="xl">
            <ReFulfillmentBillingSection
              disabled={disabled}
              input={merchantOwned}
            />
          </Flex>
        )}
      </div>
    </SettingCard>
  );
});

const ErrorBanner = memo(function ErrorBanner() {
  const { sectionErrors, sectionRefs } =
    useRequiredContext(TabNavigationContext);

  const allErrors = useMemo(() => {
    const errors: { section: string; sectionId: string; message: string }[] =
      [];
    for (const [id, sectionErrorList] of Object.entries(sectionErrors)) {
      const title = sectionRefs[id]?.title ?? id;
      for (const message of sectionErrorList) {
        errors.push({ section: title, sectionId: id, message });
      }
    }
    return errors;
  }, [sectionErrors, sectionRefs]);

  if (allErrors.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 100,
        maxWidth: 400,
      }}
    >
      <RedoAlert
        color="error"
        description={allErrors
          .map((e) => `${e.section}: ${e.message}`)
          .join(" | ")}
        title={`${allErrors.length} ${allErrors.length === 1 ? "error" : "errors"}`}
      />
    </div>
  );
});

const AutomationExistsModal = memo(function AutomationExistsModal({
  automationId,
  teamId,
  entries,
  onClose,
}: {
  automationId: string;
  teamId: string;
  entries: { accountName: string; locationNames: string[] }[];
  onClose: () => void;
}) {
  return (
    <RedoModal
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "View Automation",
        onClick: () => {
          window.open(automationUrl(teamId, automationId), "_blank");
          onClose();
        },
        size: "xs",
      }}
      secondaryButton={{ text: "Close", onClick: onClose, size: "xs" }}
      title="Automation Update Required"
    >
      <Flex dir="column" gap="lg">
        <Text>
          There is already an automation for this merchant. You will have to add
          these new locations manually:
        </Text>
        {entries.map((entry) => (
          <Flex dir="column" gap="xs" key={entry.accountName}>
            <Text fontWeight="semibold">{entry.accountName}</Text>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {entry.locationNames.map((name) => (
                <li key={name}>
                  <Text>{name}</Text>
                </li>
              ))}
            </ul>
          </Flex>
        ))}
      </Flex>
    </RedoModal>
  );
});
